import { useEffect, useState, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Star, CheckCircle2 } from 'lucide-react';
import { motion } from 'framer-motion';
import { toast } from 'sonner';

import { Textarea } from '@/components/ui/textarea';
import { Button } from '@/components/ui/button';

import { obtenerCita, valorarCita } from '@/components/hooks/useCitas';
import { useEmpresa } from '@/components/hooks/useEmpresa';

/* ========================================================================
   TIPOS LOCALES
========================================================================= */

interface CitaDetalle {
  id_cita: string;
  id_empresa: string;
  nombre_servicio: string;
  fecha_cita: string;
  hora_cita: string;
  hora_fin: string;
  tiene_valoracion?: boolean;
  puntuacion_cita?: number | null;
  comentario_cita?: string | null;
}

/* ========================================================================
   CONSTANTES
========================================================================= */

const ESTRELLAS = [1, 2, 3, 4, 5];

/* ========================================================================
   COMPONENTE PRINCIPAL
========================================================================= */

export default function RateAppointment() {
  const { id_cita } = useParams();
  const navigate = useNavigate();

  /* --------------------------------------------------------------------
     ESTADOS
  -------------------------------------------------------------------- */
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [valorada, setValorada] = useState(false);

  const [cita, setCita] = useState<CitaDetalle | null>(null);
  const [logoError, setLogoError] = useState(false);

  const [rating, setRating] = useState(0);
  const [hoverRating, setHoverRating] = useState(0);
  const [comentario, setComentario] = useState('');

  /* --------------------------------------------------------------------
     CARGAR CITA
  -------------------------------------------------------------------- */
  const cargarCita = async () => {
    if (!id_cita) {
      toast.error('Identificador de cita no válido');
      navigate('/');
      return;
    }

    const resp = await obtenerCita(id_cita);

    if (!resp.ok || !resp.cita) {
      toast.error('No se pudo cargar la cita');
      navigate('/');
      return;
    }

    const data = resp.cita as CitaDetalle;
    setCita(data);

    if (data.tiene_valoracion) {
      setValorada(true);
      setRating(data.puntuacion_cita || 0);
      setComentario(data.comentario_cita || '');
    }

    setLoading(false);
  };

  useEffect(() => {
    cargarCita();
  }, []);

  /* --------------------------------------------------------------------
     CARGAR EMPRESA (solo si ya tenemos id_empresa)
  -------------------------------------------------------------------- */
  const { empresa } = useEmpresa(cita?.id_empresa);

  /* --------------------------------------------------------------------
     MEMO: URL de logo con cache-busting
  -------------------------------------------------------------------- */
  const logoUrl = useMemo(() => {
    if (!empresa?.logo || typeof empresa.logo !== 'string') return null;
    if (!empresa.logo.trim()) return null;
    return `${empresa.logo}?t=${Date.now()}`;
  }, [empresa]);

  /* --------------------------------------------------------------------
     HANDLER: enviar valoración
  -------------------------------------------------------------------- */
  const enviarValoracion = async () => {
    if (valorada) return;

    if (rating === 0) {
      toast.error('Selecciona una cantidad de estrellas para continuar');
      window.scrollTo({ top: 0, behavior: 'smooth' });
      return;
    }

    if (!id_cita) {
      toast.error('Identificador de cita no válido');
      return;
    }

    setSaving(true);

    const resp = await valorarCita(id_cita, rating, comentario);

    if (!resp.ok) {
      toast.error('No se pudo guardar tu valoración');
      setSaving(false);
      return;
    }

    toast.success('¡Gracias por tu valoración!');
    setValorada(true);
    setSaving(false);
  };

  /* --------------------------------------------------------------------
     LOADING
  -------------------------------------------------------------------- */
  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-screen bg-gray-50 dark:bg-black">
        <div className="animate-spin h-10 w-10 border-t-2 border-[#103254] dark:border-white rounded-full" />
      </div>
    );
  }

  if (!cita) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-black text-gray-600 dark:text-gray-400">
        No se encontró la información de la cita.
      </div>
    );
  }

  /* ========================================================================
     RENDER
  ======================================================================== */

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-black px-4">
      <div className="w-full max-w-md bg-white dark:bg-[#1c1c1e] shadow-lg overflow-hidden rounded-xl">
        {/* --------------------------------------------------------------------
           CABECERA EMPRESA
        -------------------------------------------------------------------- */}
        {empresa && (
          <div className="bg-[#103254] dark:bg-[#0a1a16] text-white">
            <div className="flex flex-col items-center justify-center gap-2 py-6 px-4">
              {logoUrl && !logoError && (
                <img
                  src={logoUrl}
                  alt="logo"
                  className="w-16 h-16 object-contain bg-white p-1 rounded-full"
                  onError={() => setLogoError(true)}
                />
              )}

              <h1 className="text-lg font-semibold tracking-wide text-center">
                {empresa.empresa}
              </h1>
            </div>
          </div>
        )}

        {/* --------------------------------------------------------------------
           CONTENIDO
        -------------------------------------------------------------------- */}
        <motion.div
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          className="px-4 py-6 space-y-6"
        >
          {/* --------------------------------------------------------------------
             CITA YA VALORADA
          -------------------------------------------------------------------- */}
          {valorada ? (
            <div className="text-center space-y-4">
              <CheckCircle2 className="w-12 h-12 text-green-600 dark:text-green-400 mx-auto" />

              <h2 className="text-lg font-bold text-[#103254] dark:text-white">
                Ya has valorado esta cita
              </h2>

              <div className="flex justify-center gap-2 py-2">
                {ESTRELLAS.map((num) => (
                  <Star
                    key={num}
                    className={`w-8 h-8 ${
                      num <= rating
                        ? 'text-yellow-400 fill-yellow-400'
                        : 'text-gray-300 dark:text-gray-600'
                    }`}
                  />
                ))}
              </div>
            </div>
          ) : (
            <>
              {/* --------------------------------------------------------------------
                 TÍTULO
              -------------------------------------------------------------------- */}
              <div className="text-center">
                <p className="text-sm text-gray-600 dark:text-gray-400">
                  ¡Gracias por visitarnos!
                </p>
                <h2 className="text-xl font-bold text-[#103254] dark:text-white mt-1">
                  Valorar experiencia
                </h2>
              </div>

              {/* --------------------------------------------------------------------
                 INFO CITA
              -------------------------------------------------------------------- */}
              <div className="bg-gray-50 dark:bg-white/5 border dark:border-white/10 p-4 shadow-sm rounded-lg text-center space-y-1 text-sm">
                <p className="font-semibold text-gray-800 dark:text-gray-100">
                  {cita.nombre_servicio}
                </p>

                <p className="text-gray-600 dark:text-gray-400">
                  {new Date(cita.fecha_cita).toLocaleDateString('es-ES', {
                    weekday: 'long',
                    day: 'numeric',
                    month: 'long',
                  })}
                  {' · '}
                  {cita.hora_cita?.slice(0, 5)} / {cita.hora_fin?.slice(0, 5)}
                </p>
              </div>

              {/* --------------------------------------------------------------------
                 ESTRELLAS
              -------------------------------------------------------------------- */}
              <div className="flex justify-center gap-2 sm:gap-3 py-3">
                {ESTRELLAS.map((num) => (
                  <motion.button
                    key={num}
                    whileHover={{ scale: 1.15 }}
                    whileTap={{ scale: 0.95 }}
                    onMouseEnter={() => setHoverRating(num)}
                    onMouseLeave={() => setHoverRating(0)}
                    onClick={() => setRating(num)}
                    className="focus:outline-none"
                  >
                    <Star
                      className={`w-9 h-9 cursor-pointer ${
                        (hoverRating || rating) >= num
                          ? 'text-yellow-400 fill-yellow-400'
                          : 'text-gray-300 dark:text-gray-600'
                      }`}
                    />
                  </motion.button>
                ))}
              </div>

              {/* --------------------------------------------------------------------
                 COMENTARIO
              -------------------------------------------------------------------- */}
              <Textarea
                placeholder="Comparte tu experiencia (opcional)"
                rows={4}
                value={comentario}
                onChange={(e) => setComentario(e.target.value)}
                className="rounded-lg text-sm bg-gray-50 dark:bg-white/5 dark:border-white/10 dark:text-white dark:placeholder:text-gray-500"
              />

              {/* --------------------------------------------------------------------
                 BOTÓN
              -------------------------------------------------------------------- */}
              <Button
                disabled={saving}
                onClick={enviarValoracion}
                className="w-full rounded-lg font-medium text-white bg-yellow-500 hover:bg-yellow-600 dark:bg-yellow-600 dark:hover:bg-yellow-500 shadow-md disabled:opacity-50 py-3"
              >
                {saving ? 'Guardando…' : 'Enviar valoración'}
              </Button>
            </>
          )}

          {/* --------------------------------------------------------------------
             VOLVER AL LOGIN
          -------------------------------------------------------------------- */}
          <Button
            type="button"
            variant="outline"
            className="w-full rounded-lg dark:border-white/20 dark:text-white dark:hover:bg-white/10"
            onClick={() => navigate('/')}
          >
            Volver al inicio
          </Button>
        </motion.div>
      </div>
    </div>
  );
}